import { useEffect, useRef } from 'react';

// The SOLARI split-flap mark, standalone — no org context, no tenant logo. Same
// engine as Solari's Logo.tsx (flip-in once per page load, the aircraft flies the
// seam and lands each letter), so the chip under the tail number animates exactly
// like the product's own wordmark. CSS: the `.solari-*` block in index.css.

const WORD = ['S', 'O', 'L', 'A', 'R', 'I'];
// The board stops on a message before it stops on the name. Six cells, and
// "GO FLY" is exactly six — so the board can flash the thing the product is for
// on its way to the name. The blank cell is a
// non-breaking space so the pitch doesn't collapse mid-hold.
const WAYPOINT = ['G', 'O', '\u00A0', 'F', 'L', 'Y'];
// A BLINK, not a message, and now literally so: the board NEVER LANDS on GO
// FLY. It used to run as two passes — churn, stop dead on GO FLY, hold, churn
// again — and stopping is what made it read as a second wordmark. Every cell
// simply shows its waypoint letter for one beat MID-CHURN, all six at once so
// the phrase is legible, and the drums keep turning straight through it. There
// is no moment where the mark is at rest on anything but its own name.
const WAYPOINT_AT_MS = 150;
const WAYPOINT_BLINK_MS = 130;
// The board turns for a beat before the aircraft enters, so the flip is already
// in motion when the plane arrives to bring the letters up.
const FLIGHT_DELAY_MS = 300;
// The aircraft's path, shared with the solari-fly keyframe in index.css: the
// NOSE runs from FLY_FROM to FLY_TO of the mark's width, linearly. Keeping the
// numbers here means the landings can be solved from real letter positions
// rather than assuming evenly spaced cells.
const FLY_FROM = -0.2;
const FLY_TO = 1.2;
// Matches the .solari-flap opacity transition in index.css.
const INK_FADE_MS = 140;
// How long the aircraft takes to cross the mark. Casey, 9/18: the plane was
// loitering. The CSS no longer carries a duration of its own — this value is
// written onto the element as --solari-fly-ms, so the keyframe and the landing
// clock below can never drift apart again.
const FLIGHT_MS = 1120;
// M and W are the only letters wider than the .solari-flap cell (0.88em), so
// they'd overflow the housing mid-churn. Everything else is fair game.
const DECK = 'ABCDEFGHIJKLNOPQRSTUVXYZ';

// The board flips itself in on arrival — the one moment the identity gets to
// behave like the thing it's named after.
//
// Three details that cost more than the timing did:
//   • Every flap is pinned to the WIDEST final glyph for the duration of the
//     churn. Pinning each to its own width clips a "W" into the space of an
//     "I"; pinning nothing makes the whole wordmark jitter. Uniform pitch is
//     also what a real board has, so it reads correctly and settles into the
//     true kerning when it lands.
//   • The claim is released on cleanup. React StrictMode mounts, unmounts and
//     remounts in development, so a module flag set on the first pass left the
//     real mount with nothing to do and the animation silently never ran.
//   • Hidden instances (the mobile header at desktop width) skip their turn so
//     the visible mark gets the run.
// Played ONCE per page load, and never again. The flag is set when the last
// flap LANDS, not when the run starts — React StrictMode mounts, unmounts and
// remounts in development, and a flag set on the throwaway first pass left the
// real mount with nothing to do. Completion is the only honest signal.
//
// `flapRunning` is the separate concurrency guard: two visible marks must not
// churn at once, but a run that gets torn down mid-flight has to release so
// the next mount can finish the job.
let flapPlayed = false;
let flapRunning = false;

function useFlapIn() {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The flip is once per page load, but the SWEEP belongs on every mark: a
    // header logo on the fifth page you visit shouldn't be the only dead one.
    if (flapPlayed) {
      if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        el.classList.add('alive');
      }
      return;
    }
    if (flapRunning) return;
    // A hidden instance (the mobile header at desktop width) skips its turn so
    // the visible mark gets the run.
    if (!el.offsetParent && el.offsetWidth === 0) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    flapRunning = true;

    const flaps = Array.from(el.querySelectorAll<HTMLElement>('.solari-flap'));
    const timers: number[] = [];

    // EVERY CELL IS THE WIDTH OF ITS OWN LETTER, and stays that width for the
    // whole run. A uniform cell — which is what this used to be — parks a
    // narrow glyph in a wide box, and the I ended up floating away from the R
    // with a gap no other pair had. Measuring each cell before anything moves
    // means the kerning is correct at rest AND unchanged during the flip, so
    // nothing collapses at the end either.
    const cellW = flaps.map((f) => f.getBoundingClientRect().width);
    // Pinned in EM, not px. The mark renders at a different size in the sidebar,
    // the splash panel and the phone header, and a breakpoint or a browser zoom
    // can change that size after mount — px cells would keep their old widths
    // and the glyphs would pile into each other.
    const fontPx = parseFloat(window.getComputedStyle(el).fontSize) || 16;
    flaps.forEach((f, i) => {
      f.style.width = `${(cellW[i] / fontPx).toFixed(4)}em`;
    });

    // Per-cell decks. With cells at their true widths a wide glyph would spill
    // out of a narrow one, so each cell only ever shows characters that fit it
    // — which is also how a real board works: every drum carries its own set.
    const probe = document.createElement('span');
    const cs = window.getComputedStyle(el);
    probe.style.cssText =
      `position:absolute;visibility:hidden;white-space:pre;font:${cs.font};letter-spacing:${cs.letterSpacing};`;
    document.body.appendChild(probe);
    const glyphW = new Map<string, number>();
    for (const ch of DECK) {
      probe.textContent = ch;
      glyphW.set(ch, probe.getBoundingClientRect().width);
    }
    probe.remove();
    const decks = cellW.map((w) => {
      const fits = DECK.split('').filter((ch) => (glyphW.get(ch) ?? 0) <= w + 0.5);
      // Never hand back an empty deck — a hairline cell still has to churn.
      return fits.length ? fits : DECK.split('');
    });

    // Where each letter actually sits, now that the cells differ. The aircraft
    // flies a straight line at a constant rate, so rather than assuming evenly
    // spaced cells (they no longer are) each cell is told to LAND when the
    // aircraft reaches it. The sync survives any kerning.
    const markLeft = el.getBoundingClientRect().left;
    const centres = flaps.map((f) => {
      const r = f.getBoundingClientRect();
      return r.left - markLeft + r.width / 2;
    });
    const markW = el.getBoundingClientRect().width || 1;
    // Matches the solari-fly keyframe: nose from FLY_FROM to FLY_TO of the
    // mark's width, linear, over FLIGHT_MS.
    const noseAt = (t: number) => (FLY_FROM + (FLY_TO - FLY_FROM) * (t / FLIGHT_MS)) * markW;
    const timeAtX = (x: number) => ((x / markW - FLY_FROM) / (FLY_TO - FLY_FROM)) * FLIGHT_MS;
    void noseAt;

    // One churn-and-land pass over every cell. `settle` fires once the last
    // cell in the pass has stopped. The housing and the fixed pitch stay on
    // between passes — it's one continuous run of the board, not two.
    const runPass = (
      targetOf: (i: number) => string,
      baseSteps: number,
      done: () => void,
      // The final pass settles each cell to FULL ink as it lands. Everything
      // before that — the churn and the GO FLY hold — runs faded, so the name
      // arriving is the moment the mark goes dark. It used to churn at full
      // strength, which meant SOLARI landed at exactly the weight the board had
      // been showing all along and the reveal had nothing left to give.
      settles = false,
      // When given, cell i lands at landAt(i) ms from now instead of on the
      // fixed stagger — that's how the letters stay tied to the aircraft.
      landAt?: (i: number) => number
    ) => {
      let landed = 0;
      const t0 = performance.now();
      flaps.forEach((flap, i) => {
        let step = 0;
        let hasLanded = false;
        const deck = decks[i];
        const landTime = landAt ? Math.max(120, landAt(i)) : null;
        const steps = baseSteps + i * 2;
        const startAt = landTime ? 0 : i * 90;

        const land = () => {
          if (hasLanded) return;
          hasLanded = true;
          flap.textContent = targetOf(i);
          flap.classList.remove('churn');
          // Per-cell, so the darkening cascades left to right with the
          // landings rather than snapping on all at once.
          if (settles) flap.classList.remove('flapping');
          landed += 1;
          if (landed === flaps.length) done();
        };

        const tick = () => {
          if (hasLanded) return;
          // Without a landing clock, stop after the counted flaps.
          if (!landTime && step >= steps) return land();
          // The one beat where the drums show something on purpose. Checked
          // against the wall clock, not the tick count, so all six cells blink
          // together even though their ticks have drifted apart by then.
          const since = performance.now() - t0;
          flap.textContent =
            since >= WAYPOINT_AT_MS && since < WAYPOINT_AT_MS + WAYPOINT_BLINK_MS
              ? WAYPOINT[i] ?? ''
              : deck[Math.floor(Math.random() * deck.length)];
          flap.classList.remove('churn');
          void flap.offsetWidth; // restart the snap keyframe
          flap.classList.add('churn');
          step += 1;
          timers.push(window.setTimeout(tick, 55));
        };

        timers.push(window.setTimeout(tick, startAt));
        // THE LANDING IS ON AN ABSOLUTE CLOCK, not a count of flaps. Counting
        // 55ms ticks accumulates timer drift — by the sixth cell the letters
        // were landing far enough behind that the aircraft had already passed
        // them. The churn just runs until this fires.
        if (landTime) timers.push(window.setTimeout(land, landTime));
      });
    };

    flaps.forEach((flap) => {
      flap.classList.add('flapping'); // draws the flap housing — churn only
    });

    // ONE CONTINUOUS RUN. The cells churn from the first frame, flash GO FLY
    // together without stopping, and are then landed one by one by the
    // aircraft. Nothing waits for anything else to finish, which is the whole
    // difference between a board turning and a board pausing.
    el.style.setProperty('--solari-fly-ms', `${FLIGHT_MS}ms`);
    timers.push(
      window.setTimeout(() => {
        // The aircraft's path is timed so it passes over each cell exactly as
        // that cell lands, which makes the plane look like what's bringing the
        // letters up rather than a lap of honour flown once the name is
        // already sitting there.
        el.classList.add('scanning');
        timers.push(window.setTimeout(() => el.classList.remove('scanning'), FLIGHT_MS));
      }, FLIGHT_DELAY_MS)
    );

    runPass(
      (i) => flaps[i].dataset.final ?? '',
      5,
      () => {
        flaps.forEach((f) => f.classList.remove('churn', 'flapping'));
        flapPlayed = true;
        flapRunning = false;
        // Once the name is up and the aircraft has cleared, the mark starts
        // breathing: a slow light sweep every few seconds so a logo that's sat
        // on screen for ten minutes still looks switched on.
        timers.push(window.setTimeout(() => el.classList.add('alive'), 220));
      },
      true, // this is the pass that brings the ink up
      // Land as the nose arrives, minus half the ink fade so the letter is
      // mid-darkening under the aircraft rather than behind it.
      (i) => FLIGHT_DELAY_MS + timeAtX(centres[i]) - INK_FADE_MS / 2
    );

    return () => {
      timers.forEach(clearTimeout);
      el.classList.remove('scanning');
      flaps.forEach((f) => {
        f.textContent = f.dataset.final ?? '';
        f.classList.remove('churn', 'flapping');
      });
      if (!flapPlayed) flapRunning = false;
    };
  }, []);

  return ref;
}

type Ground = 'canvas' | 'white' | 'dark' | 'night' | 'accent';

// The colour the seam is filled with on each surface it sits on.
const SEAM: Record<Ground, string> = {
  canvas: '#F5F5F7', // app background (and the splash panel's top-left stop)
  white: '#FFFFFF', // cards, sidebar
  dark: '#123F6B', // the brand gradient's mid stop
  // The pre-auth panel's TOP stop, where the wordmark actually sits. Using
  // 'dark' there filled the seam with the mid stop and the cut read as a
  // stripe of the wrong blue across every letter.
  night: '#0B1B3A',
  accent: 'rgb(var(--accent))',
};


export function SolariMark({
  className = '',
  variant = 'navy',
  ground = 'canvas',
  style,
}: {
  className?: string;
  variant?: 'navy' | 'white';
  /** What the mark sits on — the seam is filled with that colour. */
  ground?: Ground;
  style?: React.CSSProperties;
}) {
  const markRef = useFlapIn();
  return (
    <span
      ref={markRef}
      className={`solari-mark ${variant === 'white' ? 'text-white' : 'text-navy-900'} ${className}`}
      style={
        {
          ['--seam']: SEAM[ground],
          ['--ink']: variant === 'white' ? '#FFFFFF' : '#0B1B3A',
          ['--sheen']: variant === 'white' ? '#7DD3FC' : '#3D7FB8',
          ...style,
        } as React.CSSProperties
      }
      role="img"
      aria-label="Solari"
    >
      {WORD.map((ch, i) => (
        <span key={`${ch}-${i}`} className="solari-flap" data-final={ch} aria-hidden="true">
          {ch}
        </span>
      ))}
      <span className="solari-flight" aria-hidden="true">
        <span className="solari-flight-trail" />
        <svg className="solari-flight-plane" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23 12 15.2 10.9 13.4 10.9 11.4 2 9.6 2 10.4 10.95 5.4 11.1 4.6 7 3.2 7 3.5 11.2 1.4 11.4 1.4 12.6 3.5 12.8 3.2 17 4.6 17 5.4 12.9 10.4 13.05 9.6 22 11.4 22 13.4 13.1 15.2 13.1Z" />
        </svg>
      </span>
    </span>
  );
}
